// SPDX-License-Identifier: GPL-3.0-or-later
// A decoration with a simple collider. The renderer draws the decoration from its
// mesh id. The engine only needs the cylinder that a ball can hit.
// A decoration without a collider is not an engine item.

import type { DecorationSpec, TableInfoSpec } from "../../../shared/contracts";
import { EventProxy } from "../../core/event-proxy";
import type { IHittable } from "../../core/ihittable";
import { Item } from "../../core/item";
import { type IPhysicalData, ItemData } from "../../core/item-data";
import type { Player } from "../../core/player";
import { degToRad } from "../../math/float";
import { Vertex2D } from "../../math/vertex2d";
import { HitCircle } from "../../physics/hit-circle";
import type { HitObject } from "../../physics/hit-object";
import type { Table } from "../../table-model";

export class DecorationData extends ItemData implements IPhysicalData {
  public center: Vertex2D;
  public radius: number;
  public height: number;
  /** Height of the bottom of the cylinder above the playfield. */
  public zLow: number;
  public elasticity: number;
  public elasticityFalloff: number;
  public friction: number;
  /** Degrees. */
  public scatter: number;
  public overwritePhysics: boolean = true;
  public isCollidable: boolean = true;
  public szPhysicsMaterial?: string;

  private constructor(
    name: string,
    center: Vertex2D,
    radius: number,
    height: number,
    zLow: number,
    info: TableInfoSpec,
  ) {
    super(name);
    this.center = center;
    this.radius = radius;
    this.height = height;
    this.zLow = zLow;
    this.elasticity = info.elasticity;
    this.elasticityFalloff = info.elasticityFalloff;
    this.friction = info.friction;
    this.scatter = info.scatter;
  }

  /** Builds the data from a spec. Returns undefined when the decoration has no collider. */
  public static fromSpec(spec: DecorationSpec, info: TableInfoSpec): DecorationData | undefined {
    if (!spec.collider) {
      return undefined;
    }
    return new DecorationData(
      spec.name,
      new Vertex2D(spec.position.x, spec.position.y),
      spec.collider.radius,
      spec.collider.height,
      spec.position.z,
      info,
    );
  }
}

export class Decoration extends Item<DecorationData> implements IHittable {
  private hit?: HitCircle;

  public constructor(data: DecorationData) {
    super(data);
  }

  public isCollidable(): boolean {
    return true;
  }

  public setupPlayer(player: Player, table: Table): void {
    this.events = new EventProxy(this, "decoration", player.getEventSink());
    const base = table.getTableHeight() + this.data.zLow;
    const circle = new HitCircle(this.data.center, this.data.radius, base, base + this.data.height);
    circle.setElasticity(this.data.elasticity, this.data.elasticityFalloff);
    circle.setFriction(this.data.friction);
    circle.setScatter(degToRad(this.data.scatter));
    this.hit = circle;
  }

  public getHitShapes(): HitObject[] {
    return [this.hit!];
  }
}
